// src/components/desk/AreaCard.jsx — the micro-district / quarter / settlement / neighbourhood a flat is in.
// ============================================================================
// Owner, 2026-09-21: "if we resolve address and it's inside it then this is parent and should fall inside";
// "Desk card and price comparisons first".
//
// The area is the PARENT of the resolved address (core 1654/1659/1660, property_area_v1, rebuilt hourly).
// What decided it, strongest first: the resolved address (street + house number -> the building's point),
// the area the advert names, a street lying in one area. The card says which, because an agent weighing
// the answer needs to know whether it came from the building or from a label.
//
// Two things it shows on purpose:
// - `other`: the advert names a DIFFERENT area than the resolved address. The address wins, but the
//   disagreement is the kind of thing an agent can settle with one call, so it is not hidden.
// - the pin check: portal pins are geocodes of the typed address, so "outside" is a flag on the pin,
//   never on the area.
// Renders nothing when no area is known: most flats are not in a micro-district, and "none" on every
// card would be noise.

import { useEffect, useState } from 'react'
import { getLanguage, t } from '../../lib/i18n.js'
import { getPropertyArea } from '../../db/core.js'
import { card, ROW, ROW_LEFT, ROW_VAL, SECTION_LABEL, Badge } from './primitives.jsx'

const nameOf = (x, lang) => (x ? (lang === 'ka' ? x.name_ka || x.name_en : x.name_en || x.name_ka) || x.code : null)

export default function AreaCard({ propertyId }) {
    const [data, setData] = useState(null)

    useEffect(() => {
        if (!propertyId) return
        const ac = new AbortController()
        let alive = true
        getPropertyArea(propertyId, { signal: ac.signal })
            .then(r => { if (alive) setData(r) })
            .catch(e => { if (alive && e?.name !== 'AbortError') console.error('[area]', e.message) })
        return () => { alive = false; ac.abort() }
    }, [propertyId])

    if (!data || !data.area) return null
    const lang = getLanguage()
    const { area, basis, other, pin_in_area } = data

    return (
        <div className={card}>
            <div className={SECTION_LABEL}>{t('area_section')}</div>
            <div className="mt-1 font-medium">{nameOf(area, lang)}</div>
            {area.parent && (
                <div className={ROW}>
                    <span className={ROW_LEFT}>{t('area_within')}</span>
                    <span className={ROW_VAL}>{nameOf(area.parent, lang)}</span>
                </div>
            )}
            {basis && (
                <div className={ROW}>
                    <span className={ROW_LEFT}>{t('area_decided_by')}</span>
                    <span className={ROW_VAL}>{t(`area_basis_${basis}`)}</span>
                </div>
            )}
            {other && (
                <div className={ROW}>
                    <span className={ROW_LEFT}>{t('area_advert_says')}</span>
                    <span className={ROW_VAL}><Badge variant="amber" label={nameOf(other, lang)} /></span>
                </div>
            )}
            {pin_in_area !== null && pin_in_area !== undefined && (
                <div className={ROW}>
                    <span className={ROW_LEFT}>{t('cad_our_pin')}</span>
                    <span className={ROW_VAL}>
                        {pin_in_area
                            ? <Badge variant="green" label={t('area_pin_inside')} />
                            : <Badge variant="amber" label={t('area_pin_outside')} />}
                    </span>
                </div>
            )}
        </div>
    )
}
